import i18n from '../i18n';

export type TransferMode = 'ask' | 'allow-cellular' | 'wifi-only';
export type TransferDecision = 'allow' | 'ask' | 'block';
export type TransferDirection = 'upload' | 'download';

export const HEAVY_TRANSFER_THRESHOLD_BYTES = 5 * 1024 * 1024;
const STORAGE_KEY = 'hc_transfer_mode';
let memoryMode: TransferMode | null = null;
let storageUnavailable = false;

interface NetworkInformationLike {
  type?: string;
}

/** Unknown browser/network state is deliberately not treated as cellular. */
export function isCellularNetwork(): boolean {
  try {
    if (typeof navigator === 'undefined') return false;
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
    return connection?.type === 'cellular';
  } catch {
    // Without positive cellular evidence, avoid prompting devices on Wi‑Fi or desktop.
    return false;
  }
}

export function getTransferMode(): TransferMode {
  if (!storageUnavailable) {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      if (value === 'allow-cellular' || value === 'wifi-only' || value === 'ask') return value;
    } catch {
      storageUnavailable = true;
    }
  }
  return memoryMode ?? 'ask';
}

export function setTransferMode(mode: TransferMode): void {
  memoryMode = mode;
  if (storageUnavailable) return;
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    storageUnavailable = true;
    // Keep the app working with an in-memory preference for this page session.
  }
}

export function transferDecision(mode: TransferMode, cellular: boolean, sizeBytes: number | null | undefined): TransferDecision {
  if (!cellular) return 'allow';
  if (sizeBytes != null && sizeBytes >= 0 && sizeBytes <= HEAVY_TRANSFER_THRESHOLD_BYTES) return 'allow';
  if (mode === 'allow-cellular') return 'allow';
  return mode === 'wifi-only' ? 'block' : 'ask';
}

export function isHeavyTransfer(sizeBytes: number | null | undefined): boolean {
  return sizeBytes == null || sizeBytes < 0 || sizeBytes > HEAVY_TRANSFER_THRESHOLD_BYTES;
}

export interface TransferRequest {
  direction: TransferDirection;
  label: string;
  sizeBytes: number | null;
}

export interface TransferAuthorizationResult {
  allowed: boolean;
  reason?: string;
}

export type TransferPromptListener = (request: TransferRequest | null) => void;

interface PendingTransfer {
  request: TransferRequest;
  resolve: (result: TransferAuthorizationResult) => void;
}

const pendingTransfers: PendingTransfer[] = [];
const promptListeners = new Set<TransferPromptListener>();
const noticeListeners = new Set<(message: string | null) => void>();
let activePrompt: PendingTransfer | null = null;
let activeNoticeTimer: ReturnType<typeof setTimeout> | null = null;

function notifyPromptListeners(): void {
  const request = activePrompt?.request ?? null;
  promptListeners.forEach((listener) => listener(request));
}

function pumpTransferQueue(): void {
  if (activePrompt) return;
  while (pendingTransfers.length > 0) {
    const pending = pendingTransfers.shift()!;
    const decision = transferDecision(getTransferMode(), isCellularNetwork(), pending.request.sizeBytes);
    if (decision === 'allow') {
      pending.resolve({ allowed: true });
      continue;
    }
    if (decision === 'block') {
      pending.resolve({ allowed: false, reason: i18n.t('misc2:transferPolicyWifiOnly') });
      continue;
    }
    activePrompt = pending;
    notifyPromptListeners();
    return;
  }
}

/** Preflight a single transfer. The returned promise resolves only after the user chooses a mode. */
export function requestTransferAuthorization(request: TransferRequest): Promise<TransferAuthorizationResult> {
  const decision = transferDecision(getTransferMode(), isCellularNetwork(), request.sizeBytes);
  if (decision === 'allow') return Promise.resolve({ allowed: true });
  if (decision === 'block') {
    return Promise.resolve({
      allowed: false,
      reason: i18n.t('misc2:transferPolicyWifiOnly'),
    });
  }
  return new Promise((resolve) => {
    pendingTransfers.push({ request, resolve });
    pumpTransferQueue();
  });
}

export function subscribeTransferPrompt(listener: TransferPromptListener): () => void {
  promptListeners.add(listener);
  listener(activePrompt?.request ?? null);
  return () => promptListeners.delete(listener);
}

/** null dismisses without changing Ask; saved choices apply to future heavy transfers on this client. */
export function subscribeTransferNotice(listener: (message: string | null) => void): () => void {
  noticeListeners.add(listener);
  return () => noticeListeners.delete(listener);
}

export function showTransferNotice(message: string): void {
  noticeListeners.forEach((listener) => listener(message));
  if (activeNoticeTimer) clearTimeout(activeNoticeTimer);
  activeNoticeTimer = setTimeout(() => {
    activeNoticeTimer = null;
    noticeListeners.forEach((listener) => listener(null));
  }, 5000);
}

export function answerTransferPrompt(mode: TransferMode | null): void {
  const current = activePrompt;
  if (!current) return;
  activePrompt = null;
  if (mode) setTransferMode(mode);
  current.resolve(mode === 'allow-cellular'
    ? { allowed: true }
    : { allowed: false, reason: mode === 'wifi-only'
      ? i18n.t('misc2:transferPolicyWifiOnly')
      : i18n.t('misc2:transferPolicyCancelled') });
  notifyPromptListeners();
  pumpTransferQueue();
}
